import { useEffect, useState } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { useFeedback } from '../components/common/Feedback';
import { AppLayout } from '../components/Layout/AppLayout';
import { CertificateStatusBadge } from '../components/Certificates/CertificateStatusBadge';
import { CertificateUploadModal } from '../components/Certificates/CertificateUploadModal';
import { CertificateAccessModal } from '../components/Certificates/CertificateAccessModal';
import { CertificateSignModal } from '../components/Certificates/CertificateSignModal';
import { CertificateEditModal } from '../components/Certificates/CertificateEditModal';
import { LoadingState, EmptyState, ErrorState } from '../components/common/FeedbackStates';
import { useAuth } from '../hooks/useAuth';
import { useCertificates } from '../hooks/useCertificates';
import { useMembers } from '../hooks/useMembers';
import { useHolders } from '../hooks/useHolders';
import { RECURSO_CERTIFICADOS, temRecurso } from '../utils/features';
import { formatarValidade } from '../utils/certificateStatus';
import { SEM_TITULAR, agruparPorTitular, chaveDaPasta } from '../utils/holders';
import {
  certificadoCombinaComBusca,
  guardarOrganizacao,
  lerOrganizacao,
  quantidadeDeCertificados,
  titularSugerido,
  type OrganizacaoDosCertificados,
} from '../utils/certificateFolders';
import { FileKey, Plus, X, AlertTriangle, PenLine, Pencil, Search, Folder, List, ChevronRight } from 'lucide-react';

export function Certificates() {
  const { confirmar } = useFeedback();
  const { membership } = useAuth();
  const liberado = temRecurso(membership, RECURSO_CERTIFICADOS);
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';

  const { certificates, isLoading, error, fetchCertificates, uploadCertificate, signPdf, updateCertificate, revokeCertificate, grantAccess, revokeAccess } = useCertificates();
  const { members, fetchMembers } = useMembers();
  const { holders, fetchHolders } = useHolders();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [accessCertId, setAccessCertId] = useState<string | null>(null);
  const [signCertId, setSignCertId] = useState<string | null>(null);
  const [editCertId, setEditCertId] = useState<string | null>(null);
  // Aviso de vencimento devolvido no cadastro, ou erro ao remover do Lictor.
  const [notice, setNotice] = useState<{ tipo: 'aviso' | 'erro'; texto: string } | null>(null);
  // D23: "pastas" (por titular, padrão) ou "lista" (todos), lembrada no navegador.
  const [organizacao, setOrganizacao] = useState<OrganizacaoDosCertificados>(lerOrganizacao);
  // Pasta aberta na visão Pastas (id do titular ou SEM_TITULAR); null = vendo as pastas.
  const [pastaAberta, setPastaAberta] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!liberado) return;
    fetchCertificates();
    fetchHolders();
    if (isAdmin) fetchMembers();
  }, [liberado, isAdmin, fetchCertificates, fetchHolders, fetchMembers]);

  useEffect(() => {
    guardarOrganizacao(organizacao);
  }, [organizacao]);

  const handleRevoke = async (id: string, label: string) => {
    if (!(await confirmar({
      titulo: `Remover o certificado “${label}” do Lictor?`,
      mensagem: 'Isto não cancela o certificado: ele continua válido e funcionando. ' +
        'O Lictor apaga a cópia que guardou (o arquivo e a senha), e ninguém do escritório poderá mais usá-lo por aqui. ' +
        'Para usá-lo de novo no Lictor, basta cadastrá-lo outra vez.',
      confirmar: 'Remover certificado',
      perigo: true,
    }))) return;
    try {
      await revokeCertificate(id);
      setNotice(null);
    } catch (err: any) {
      setNotice({ tipo: 'erro', texto: err.message || 'Erro ao remover o certificado do Lictor.' });
    }
  };

  if (!liberado) {
    return (
      <AppLayout>
        <div style={{ color: 'var(--color-text-secondary)' }}>Este recurso não está disponível para a sua organização.</div>
      </AppLayout>
    );
  }

  const accessCert = certificates.find((c) => c.id === accessCertId) ?? null;
  const signCert = certificates.find((c) => c.id === signCertId) ?? null;
  const editCert = certificates.find((c) => c.id === editCertId) ?? null;
  const membersDoAcesso = members.filter((m) => m.role === 'member' && m.active && !m.is_pending);

  // D23: mesma organização por titular da página de credenciais. Owner/admin veem também as pastas
  // vazias (para cadastrar o certificado de quem ainda não tem); member, só as dos certificados que vê.
  const emPastas = organizacao === 'pastas';
  const buscando = search.trim() !== '';
  const pastas = agruparPorTitular(certificates, holders, isAdmin);
  const pastaAtual = pastas.find((p) => p.chave === pastaAberta) || null;
  const mostrandoPastas = emPastas && !buscando && !pastaAtual;
  // A busca procura em todos os certificados, também na visão Pastas (e mostra a pasta de cada um).
  const encontrados = certificates.filter((c) => certificadoCombinaComBusca(c, search));
  const visiveis = emPastas && !buscando && pastaAtual
    ? certificates.filter((c) => chaveDaPasta(c) === pastaAtual.chave)
    : encontrados;
  const pastaDoResultado = (holderName: string | null) => (emPastas && buscando ? holderName || 'Sem titular' : null);
  const titularDoCadastro = titularSugerido(emPastas && !buscando && pastaAtual ? pastaAtual.chave : null, holders);

  const botaoDaVisao = (opcao: OrganizacaoDosCertificados) => (
    <button
      key={opcao}
      type="button"
      onClick={() => { setOrganizacao(opcao); setPastaAberta(null); }}
      title={opcao === 'pastas' ? 'Ver por titular (pastas)' : 'Ver todos os certificados'}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
        background: organizacao === opcao ? 'var(--color-bg-surface)' : 'transparent',
        color: organizacao === opcao ? 'var(--color-primary-400)' : 'var(--color-text-secondary)',
        boxShadow: organizacao === opcao ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
      }}
    >
      {opcao === 'pastas' ? <><Folder size={14} /> Pastas</> : <><List size={14} /> Lista</>}
    </button>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Certificados"
        description="Certificados digitais A1 guardados pelo escritório."
        actions={isAdmin ? (
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setIsUploadOpen(true)}>
            <Plus size={16} /> Cadastrar certificado
          </button>
        ) : undefined}
      />

      {notice && (
        <div
          role="alert"
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-6)',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            background: notice.tipo === 'erro' ? 'var(--color-error-muted)' : 'rgba(245, 158, 11, 0.08)',
            border: notice.tipo === 'erro' ? '1px solid var(--color-error)' : '1px solid rgba(245, 158, 11, 0.25)',
            color: notice.tipo === 'erro' ? 'var(--color-error)' : 'var(--color-text-primary)',
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0, color: notice.tipo === 'erro' ? 'var(--color-error)' : '#f59e0b' }} />
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{notice.texto}</span>
          <button type="button" onClick={() => setNotice(null)} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>
      )}

      {certificates.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', background: 'var(--color-bg-elevated)', padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
            <input
              type="text"
              placeholder="Buscar certificado..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: 'var(--space-2) var(--space-4) var(--space-2) var(--space-10)', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', fontSize: 'var(--text-sm)', outline: 'none', boxShadow: 'var(--shadow-sm)' }}
            />
            <Search size={16} style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
          </div>
          {/* Pastas (por titular) x Lista (todos) */}
          <div style={{ display: 'flex', flexShrink: 0, background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', padding: '2px', gap: '2px' }}>
            {(['pastas', 'lista'] as OrganizacaoDosCertificados[]).map(botaoDaVisao)}
          </div>
        </div>
      )}

      {isLoading && certificates.length === 0 ? (
        <LoadingState message="Carregando certificados..." />
      ) : error && certificates.length === 0 ? (
        <ErrorState message={error} onRetry={fetchCertificates} />
      ) : certificates.length === 0 ? (
        <EmptyState title="Nenhum certificado cadastrado ainda." description={isAdmin ? 'Use o botão "Cadastrar certificado" para começar.' : 'Quando você receber acesso a um certificado, ele aparece aqui.'} />
      ) : encontrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-4)', color: 'var(--color-text-secondary)' }}>
          <Search size={48} style={{ margin: '0 auto var(--space-4) auto', opacity: 0.5 }} />
          <p style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)' }}>Nenhum certificado encontrado</p>
          <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)', overflowWrap: 'anywhere' }}>Não encontramos resultados para "{search}". Tente buscar por outro termo.</p>
        </div>
      ) : mostrandoPastas ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 'var(--space-4)' }}>
          {pastas.map((pasta) => (
            <button
              key={pasta.chave}
              type="button"
              onClick={() => setPastaAberta(pasta.chave)}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-5)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', textAlign: 'left', color: 'var(--color-text-primary)', minWidth: 0 }}
            >
              <Folder size={28} style={{ color: pasta.chave === SEM_TITULAR ? 'var(--color-text-secondary)' : 'var(--color-primary-400)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', overflowWrap: 'anywhere' }}>{pasta.nome}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
                  {[pasta.oab, quantidadeDeCertificados(pasta.quantidade)].filter(Boolean).join(' · ')}
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          {emPastas && pastaAtual && !buscando && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
              <button type="button" onClick={() => setPastaAberta(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary-400)', cursor: 'pointer', padding: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                Certificados
              </button>
              <ChevronRight size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', minWidth: 0, overflowWrap: 'anywhere' }}>{pastaAtual.nome}</span>
              {pastaAtual.oab && <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>{pastaAtual.oab}</span>}
            </div>
          )}
          {visiveis.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              Nenhum certificado nesta pasta.{isAdmin && ' Use "Cadastrar certificado" para cadastrar um aqui.'}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 350px), 1fr))', gap: 'var(--space-6)' }}>
              {visiveis.map((cert) => (
                <div key={cert.id} style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', minWidth: 0, opacity: cert.active ? 1 : 0.7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                    <FileKey size={24} color="var(--color-primary-400)" style={{ flexShrink: 0 }} />
                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', flex: '1 1 140px', minWidth: 0, overflowWrap: 'anywhere' }}>{cert.label}</h3>
                    <CertificateStatusBadge certificate={cert} />
                  </div>
                  {pastaDoResultado(cert.holder_name) && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-400)', marginBottom: 'var(--space-3)', overflowWrap: 'anywhere' }}>Pasta: {pastaDoResultado(cert.holder_name)}</span>
                  )}
                  <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-sm)', margin: 0, marginBottom: 'var(--space-4)', flex: 1 }}>
                    <dt style={{ color: 'var(--color-text-secondary)' }}>Titular</dt>
                    <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{cert.subject_cn}</dd>
                    <dt style={{ color: 'var(--color-text-secondary)' }}>Emissor</dt>
                    <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{cert.issuer_cn}</dd>
                    <dt style={{ color: 'var(--color-text-secondary)' }}>Validade</dt>
                    <dd style={{ margin: 0 }}>{formatarValidade(cert.not_after)}</dd>
                    {cert.can_manage && cert.active && (
                      <>
                        <dt style={{ color: 'var(--color-text-secondary)' }}>Acesso</dt>
                        <dd style={{ margin: 0 }}>{cert.access.length} {cert.access.length === 1 ? 'membro' : 'membros'}</dd>
                      </>
                    )}
                  </dl>
                  {cert.can_use && (
                    <button
                      onClick={() => setSignCertId(cert.id)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-2)', marginBottom: cert.can_manage && cert.active ? 'var(--space-3)' : 0, background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}
                    >
                      <PenLine size={16} /> Assinar PDF
                    </button>
                  )}
                  {cert.can_manage && cert.active && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                      <button onClick={() => setAccessCertId(cert.id)} style={{ flex: '1 1 auto', padding: 'var(--space-2)', background: 'transparent', border: '1px solid var(--color-primary-600)', color: 'var(--color-primary-400)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
                        Gerenciar Acessos
                      </button>
                      <button onClick={() => setEditCertId(cert.id)} style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-4)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
                        <Pencil size={14} /> Editar
                      </button>
                      <button onClick={() => handleRevoke(cert.id, cert.label)} style={{ flex: '1 1 auto', padding: 'var(--space-2) var(--space-4)', background: 'transparent', border: '1px solid var(--color-error)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
                        Remover do Lictor
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isUploadOpen && (
        <CertificateUploadModal
          onClose={() => setIsUploadOpen(false)}
          onUpload={uploadCertificate}
          onUploaded={(resultado) => setNotice(resultado.warning ? { tipo: 'aviso', texto: resultado.warning } : null)}
          holders={holders}
          defaultHolderId={titularDoCadastro}
        />
      )}

      {editCert && (
        <CertificateEditModal
          certificate={editCert}
          holders={holders}
          onClose={() => setEditCertId(null)}
          onSave={(alteracoes) => updateCertificate(editCert.id, alteracoes)}
        />
      )}

      {signCert && (
        <CertificateSignModal
          certificate={signCert}
          onClose={() => setSignCertId(null)}
          onSign={(dados, certificateId) => signPdf(certificateId, dados)}
        />
      )}

      {accessCert && (
        <CertificateAccessModal
          certificate={accessCert}
          members={membersDoAcesso}
          onClose={() => setAccessCertId(null)}
          onGrant={(userId) => grantAccess(accessCert.id, userId)}
          onRevoke={(userId) => revokeAccess(accessCert.id, userId)}
          onSaved={fetchCertificates}
        />
      )}
    </AppLayout>
  );
}
